import { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Modal, Select, Slider } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { get } from 'lodash';
import styled from 'styled-components';

import { getMarks } from '@/components/CapacitySlider';
import { resolveOptionDisplayValue } from '@/delivery-type-plugin';
import {
  MAAS_INPUT_PLACEHOLDER,
  MAAS_SELECT_PLACEHOLDER,
  maasRequiredRules,
  maasRequiredSelectRules,
} from '@/delivery-type-plugin/capabilities/maas-init/form-messages';
import {
  getMinCreateDataVolumeStorageGiFromBytes,
  parseStorageQuantityToGi,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/quantity';
import {
  APP_DATA_VOLUME_NAME,
  MODEL_DATA_VOLUME_NAME,
  VOLUME_SOURCE_PVC_TEMPLATE,
  getVolumeMountPath,
  getVolumePvcTemplate,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import {
  DEFAULT_QUANTITY_UNITS,
  OptionMetaRootField,
  ReclaimPolicyFormField,
  StorageClassFormField,
  buildDiskPricingInitPatch,
  formatVendorDeviceLabel,
  isDiskPricingActive,
  isReclaimPolicyLocked,
  normalizeVendorDeviceMeta,
  readDiskPricingMeta,
  resolveReclaimPolicyValue,
  syncOptionMetaFromRaw,
  useWatchedQuantityUnit,
} from '@/delivery-type-plugin/components';
import { DISK_PRICING_FIELD } from '@/delivery-type-plugin/components/vendorDevice';
import { readOptionMeta } from '@/delivery-type-plugin/platform/option-meta';
import { useOptionalResourceFormContext } from '@/delivery-type-plugin/platform/resource-form';
import { getModalPopupContainer } from '@/delivery-type-plugin/utils/modalPopupContainer';
import { isVendorQuantityOptional } from '@/delivery-type-plugin/utils/postPaidPricing';
import { safeParseJSON } from '@/utils/json-parse';

interface StorageClassOptionItem {
  label: string;
  value: string;
  description?: string;
  provisioner?: string;
}

enum Field {
  VOLUME_NAME = 'volumeName',
  STORAGE_CLASS = 'storageClassName',
  ACCESS_MODES = 'accessModes',
  STORAGE = 'storage',
  RECLAIM_POLICY = 'reclaimPolicy',
  MOUNT_PATH = 'mountPath',
}

const MODE_OPTIONS = ['ReadWriteOnce', 'ReadOnlyMany', 'ReadWriteMany'];

/** 模型实例创建数据盘时固定的访问模式（ReadWriteMany） */
export const MODEL_DATA_DISK_ACCESS_MODES = ['ReadWriteMany'];

interface MaasDataDiskCreateModalProps {
  open?: boolean;
  initVolume?: Record<string, unknown>;
  modelSizeBytes?: number;
  defaultMountPath?: string;
  volumeName?: string;
  /** 展示可编辑卷名称（应用实例）；与 volumeName 互斥 */
  showVolumeName?: boolean;
  /** 新建时默认卷名称 */
  defaultVolumeName?: string;
  hideMountPath?: boolean;
  /** 固定访问模式且不可改（模型实例使用 ReadWriteMany） */
  fixedAccessModes?: string[];
  /** 存储字段之前的扩展内容（如模型实例的模型选择） */
  beforeContent?: ReactNode;
  /** 保存前额外校验（如模型字段） */
  onValidateExtra?: () => Promise<void>;
  /** 弹窗标题，默认「创建数据盘」 */
  title?: string;
  /** SKU / 下单计价：计费模型 ID */
  billModelId?: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  /** SKU / 下单计价：资源编码（模型/应用实例数据盘为 DataVolume） */
  resourceCode?: string;
  /** 付费时机；后付费时容量非必填 */
  paymentTiming?: string;
  skuType?: string;
  resourceEditMode?: string;
  /** SKU 外层表单；与 resourceInnerForm 同时传入时，确认保存后触发 DataVolume 算价 */
  skuOuterForm?: FormInstance;
  /** 资源配置 innerForm（模型/应用实例表单） */
  resourceInnerForm?: FormInstance;
  /**
   * 弹窗确认后主动询价（SKU pages ResourceFormContext 与 platform 非同实例时需显式传入）。
   * 未传时回退 platform context.calculatePriceData。
   */
  onPriceChange?: (outerValues: Record<string, unknown>, innerForm: FormInstance) => void | Promise<void>;
  onClose?: () => void;
  onSave?: (volume: Record<string, unknown>) => void;
}

function resolveVolumeName(
  values: Record<string, unknown>,
  options: {
    showVolumeName?: boolean;
    volumeName?: string;
    defaultVolumeName?: string;
    initVolume?: Record<string, unknown>;
  },
) {
  if (options.showVolumeName) {
    const fromForm = String(values[Field.VOLUME_NAME] ?? '').trim();
    return fromForm || options.defaultVolumeName || options.initVolume?.name || APP_DATA_VOLUME_NAME;
  }
  return options.volumeName || options.initVolume?.name || MODEL_DATA_VOLUME_NAME;
}

function parseStorageClassOriginalField(raw: unknown): StorageClassOptionItem | undefined {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'object') return raw as StorageClassOptionItem;
  return safeParseJSON(String(raw), undefined) as StorageClassOptionItem | undefined;
}

function readStorageClassName(values: Record<string, unknown>): string | undefined {
  const raw = values[Field.STORAGE_CLASS];
  if (raw == null || raw === '') return undefined;
  const trimmed = String(raw).trim();
  return trimmed || undefined;
}

function buildStorageClassOriginal(
  values: Record<string, unknown>,
  initVolume?: Record<string, unknown>,
): StorageClassOptionItem | undefined {
  const pricing = normalizeVendorDeviceMeta(readDiskPricingMeta(values));
  const storageClass = readStorageClassName(values);
  if (pricing && storageClass) {
    return { label: formatVendorDeviceLabel(pricing), value: storageClass };
  }
  if (storageClass != null && String(storageClass).trim()) {
    const label = resolveOptionDisplayValue(values, Field.STORAGE_CLASS, storageClass) || String(storageClass);
    const optionMeta = readOptionMeta(values, Field.STORAGE_CLASS);
    return {
      label,
      value: String(storageClass),
      ...(optionMeta?.provisioner ? { provisioner: String(optionMeta.provisioner) } : {}),
    };
  }
  return parseStorageClassOriginalField(initVolume?.storageClassNameOriginal);
}

function buildVolumeFromForm(
  values: Record<string, unknown>,
  initVolume?: Record<string, unknown>,
  options?: {
    volumeName?: string;
    showVolumeName?: boolean;
    defaultVolumeName?: string;
    defaultMountPath?: string;
    hideMountPath?: boolean;
    lockReclaimPolicy?: boolean;
  },
) {
  const { volumeName, showVolumeName, defaultVolumeName, defaultMountPath = '/models', hideMountPath, lockReclaimPolicy } = options || {};
  const storageGi = Number(values[Field.STORAGE]) || 10;
  const mountPath = hideMountPath ? defaultMountPath : values[Field.MOUNT_PATH];
  const storageClassNameOriginal = buildStorageClassOriginal(values, initVolume);
  const pricing = normalizeVendorDeviceMeta(readDiskPricingMeta(values));
  const storageClassName = readStorageClassName(values);
  const volume: Record<string, unknown> = {
    name: resolveVolumeName(values, { showVolumeName, volumeName, defaultVolumeName, initVolume }),
    type: 'Data',
    reclaimPolicy: resolveReclaimPolicyValue(values[Field.RECLAIM_POLICY] as string | undefined, lockReclaimPolicy),
    source: {
      type: VOLUME_SOURCE_PVC_TEMPLATE,
      mountPath,
      persistentVolumeClaimTemplate: {
        storageClassName,
        accessModes: values[Field.ACCESS_MODES],
        resources: { requests: { storage: `${storageGi}Gi` } },
      },
    },
  };
  if (storageClassNameOriginal) {
    volume.storageClassNameOriginal = storageClassNameOriginal;
  }
  if (pricing) {
    volume.pricing = pricing;
  }
  return volume;
}

function volumeToFormValues(
  volume?: Record<string, unknown>,
  defaultMountPath = '/models',
  defaultVolumeName = APP_DATA_VOLUME_NAME,
  lockReclaimPolicy = false,
) {
  const mountPath = getVolumeMountPath(volume) || defaultMountPath;
  const template = getVolumePvcTemplate(volume);
  const storageRaw = get(template, 'resources.requests.storage');
  const storageGi = parseStorageQuantityToGi(storageRaw) || 10;
  return {
    [Field.VOLUME_NAME]: volume?.name || defaultVolumeName,
    [Field.STORAGE_CLASS]: get(template, 'storageClassName'),
    [Field.ACCESS_MODES]: get(template, 'accessModes') || [MODE_OPTIONS[0]],
    [Field.STORAGE]: storageGi,
    [Field.RECLAIM_POLICY]: resolveReclaimPolicyValue(volume?.reclaimPolicy as string | undefined, lockReclaimPolicy),
    [Field.MOUNT_PATH]: mountPath,
    ...buildDiskPricingInitPatch(volume?.pricing as Parameters<typeof buildDiskPricingInitPatch>[0]),
  };
}

export function MaasDataDiskCreateModal({
  open,
  initVolume,
  modelSizeBytes,
  defaultMountPath = '/',
  volumeName,
  showVolumeName,
  defaultVolumeName = APP_DATA_VOLUME_NAME,
  hideMountPath,
  fixedAccessModes,
  beforeContent,
  onValidateExtra,
  title,
  billModelId: billModelIdProp,
  deploymentTypeId: deploymentTypeIdProp,
  resourceCode: resourceCodeProp,
  paymentTiming: paymentTimingProp,
  skuType: skuTypeProp,
  resourceEditMode: resourceEditModeProp,
  skuOuterForm,
  resourceInnerForm,
  onPriceChange,
  onClose,
  onSave,
}: MaasDataDiskCreateModalProps) {
  const { t } = useTranslation('cluster');
  const resourceFormCtx = useOptionalResourceFormContext();
  /** props 优先：SKU 页 ResourceFormProvider 与 platform context 非同实例 */
  const billModelId = billModelIdProp ?? resourceFormCtx?.billModelId;
  const deploymentTypeId = deploymentTypeIdProp ?? resourceFormCtx?.deploymentTypeId;
  const resourceCode = resourceCodeProp ?? (billModelId ? 'DataVolume' : undefined);
  const paymentTiming = paymentTimingProp ?? resourceFormCtx?.paymentTiming;
  const skuType = skuTypeProp ?? resourceFormCtx?.skuType;
  const resourceEditMode = resourceEditModeProp ?? resourceFormCtx?.resourceEditMode;
  const reclaimLocked = isReclaimPolicyLocked(resourceEditMode);
  const resolvePriceChange = onPriceChange ?? resourceFormCtx?.calculatePriceData;
  const modalTitle = title ?? t('components:create.data.disk');
  const [form] = Form.useForm();
  const storageClassRequiredMessage = t('common:validate.required.select', { name: t('common:storage.class') });
  const pricingActive = isDiskPricingActive({ billModelId, resourceCode });
  const skipSizeRequired = isVendorQuantityOptional({ billModelId, paymentTiming, skuType, resourceEditMode });
  const quantityUnit = useWatchedQuantityUnit(form, pricingActive ? DISK_PRICING_FIELD : undefined, DEFAULT_QUANTITY_UNITS.disk, {
    lockDisplayToDefault: true,
  });

  const minStorageGi = useMemo(() => getMinCreateDataVolumeStorageGiFromBytes(modelSizeBytes), [modelSizeBytes]);

  useEffect(() => {
    if (!open) return;
    const values = volumeToFormValues(initVolume, defaultMountPath, defaultVolumeName, reclaimLocked);
    if (fixedAccessModes?.length) {
      values[Field.ACCESS_MODES] = fixedAccessModes;
    }
    form.setFieldsValue(values);
    const storageClassOriginal = parseStorageClassOriginalField(initVolume?.storageClassNameOriginal);
    if (storageClassOriginal?.value) {
      syncOptionMetaFromRaw(form, storageClassOriginal.value, {
        label: storageClassOriginal.label,
        name: storageClassOriginal.label,
      });
    }
    if (showVolumeName && !initVolume) {
      const currentName = form.getFieldValue(Field.VOLUME_NAME);
      if (!currentName) {
        form.setFieldValue(Field.VOLUME_NAME, defaultVolumeName);
      }
    }
    const requiredMinGi = minStorageGi > 1 ? minStorageGi : 1;
    const currentStorageGi = Number(form.getFieldValue(Field.STORAGE));
    if (!currentStorageGi || currentStorageGi < requiredMinGi) {
      form.setFieldValue(Field.STORAGE, requiredMinGi);
    }
  }, [defaultMountPath, defaultVolumeName, fixedAccessModes, form, initVolume, minStorageGi, open, reclaimLocked, showVolumeName]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onClose?.();
  }, [form, onClose]);

  const handleSave = useCallback(async () => {
    try {
      await form.validate();
      if (onValidateExtra) {
        await onValidateExtra();
      }
    } catch {
      return;
    }
    /** getFieldsValue 可能漏计价叶子字段；与完整 store 合并后再组卷 */
    const nested = form.getFieldsValue() as Record<string, unknown>;
    const store = form.getFields() as Record<string, unknown>;
    const pricing = normalizeVendorDeviceMeta(readDiskPricingMeta(store) || readDiskPricingMeta(nested));
    const requiredMinGi = minStorageGi > 1 ? minStorageGi : 1;
    const values: Record<string, unknown> = {
      ...nested,
      ...(pricing ? buildDiskPricingInitPatch(pricing) : {}),
      [Field.STORAGE_CLASS]: nested[Field.STORAGE_CLASS] ?? get(store, Field.STORAGE_CLASS) ?? get(nested, Field.STORAGE_CLASS),
      [Field.STORAGE]: Math.max(Number(nested[Field.STORAGE]) || 10, requiredMinGi),
    };
    onSave?.(
      buildVolumeFromForm(values, initVolume, {
        volumeName,
        showVolumeName,
        defaultVolumeName,
        defaultMountPath,
        hideMountPath,
        lockReclaimPolicy: reclaimLocked,
      }),
    );
    /** onSave 已同步写回 innerForm；微任务内询价，确保 resourceConfigs 读到最新卷 */
    if (pricingActive && skuOuterForm && resourceInnerForm && resolvePriceChange) {
      const outerValues = skuOuterForm.getFieldsValue() as Record<string, unknown>;
      queueMicrotask(() => {
        void resolvePriceChange(outerValues, resourceInnerForm);
      });
    }
    handleCancel();
  }, [
    defaultMountPath,
    defaultVolumeName,
    form,
    handleCancel,
    hideMountPath,
    initVolume,
    minStorageGi,
    onSave,
    onValidateExtra,
    pricingActive,
    reclaimLocked,
    resolvePriceChange,
    resourceInnerForm,
    showVolumeName,
    skuOuterForm,
    volumeName,
  ]);

  return (
    <StyledModal title={modalTitle} visible={open} onCancel={handleCancel} onOk={handleSave} focusLock={false}>
      {beforeContent}
      <Form form={form} layout="vertical">
        {showVolumeName ? (
          <Form.Item label={t('common:volume.name')} field={Field.VOLUME_NAME} rules={maasRequiredRules(t('common:volume.name'))}>
            <Input placeholder={MAAS_INPUT_PLACEHOLDER} />
          </Form.Item>
        ) : null}

        <StorageClassFormField
          form={form}
          field={Field.STORAGE_CLASS}
          enabled={Boolean(open)}
          billModelId={billModelId}
          deploymentTypeId={deploymentTypeId}
          resourceCode={resourceCode}
          getPopupContainer={getModalPopupContainer}
          requiredMessage={storageClassRequiredMessage}
          placeholder={MAAS_SELECT_PLACEHOLDER}
          filterProvisioner="HostPath"
        />
        <OptionMetaRootField />
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Form.Item
            label={t('volume-capacity')}
            field={Field.STORAGE}
            rules={skipSizeRequired ? undefined : maasRequiredRules(t('volume-capacity'))}
            initialValue={Math.max(10, minStorageGi)}
          >
            <Slider
              showInput={{
                hideControl: false,
                style: { width: 90, marginLeft: 16 },
              }}
              marks={getMarks(minStorageGi, 1024 * 20, true, quantityUnit)}
              max={1024 * 20}
              min={minStorageGi}
            />
          </Form.Item>
          <div style={{ marginTop: 0, height: 32 }}>{quantityUnit}</div>
        </div>
        <Form.Item label={t('access-mode')} field={Field.ACCESS_MODES} rules={maasRequiredSelectRules(t('access-mode'))}>
          {fixedAccessModes?.length ? (
            <Select disabled mode="multiple" placeholder={MAAS_SELECT_PLACEHOLDER}>
              {fixedAccessModes.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <Select showSearch mode="multiple" placeholder={MAAS_SELECT_PLACEHOLDER}>
              {MODE_OPTIONS.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
        <ReclaimPolicyFormField
          form={form}
          field={Field.RECLAIM_POLICY}
          enabled={Boolean(open)}
          lockedToDelete={reclaimLocked}
          getPopupContainer={getModalPopupContainer}
          requiredMessage={t('common:validate.required.select', { name: t('common:reclaim.policy') })}
          placeholder={MAAS_SELECT_PLACEHOLDER}
        />
        {!hideMountPath ? (
          <Form.Item
            label={t('data-center:maas.model.mountPath')}
            field={Field.MOUNT_PATH}
            rules={maasRequiredRules(t('data-center:maas.model.mountPath'))}
          >
            <Input placeholder={MAAS_INPUT_PLACEHOLDER} />
          </Form.Item>
        ) : null}
      </Form>
    </StyledModal>
  );
}

const StyledModal = styled(Modal)`
  width: 800px;
  .arco-modal-content {
    min-height: 360px;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
  }
`;
